import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import './styles/globals.css';

// Eagerly loaded (needed on first paint)
import Landing from './components/landing/Landing';
import AuthPage from './components/auth/AuthPage';

// Lazy-loaded routes
const Library = lazy(() => import('./components/library/Library'));
const BookCreator = lazy(() => import('./components/book-creator/BookCreator'));
const BookReader = lazy(() => import('./components/reader/BookReader'));
const PricingPage = lazy(() => import('./components/billing/PricingPage'));
const UserOrdersPage = lazy(() => import('./components/orders/UserOrdersPage'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const BlogPostEditor = lazy(() => import('./components/admin/BlogPostEditor'));
const LandingPageEditor = lazy(() => import('./components/admin/LandingPageEditor'));
const PublicLandingPage = lazy(() => import('./components/landing/PublicLandingPage'));
const EmailTemplateEditor = lazy(() => import('./components/admin/EmailTemplateEditor'));

// Admin tab pages
const AnalyticsTab = lazy(() => import('./components/admin/tabs/AnalyticsTab'));
const SiteAnalyticsTab = lazy(() => import('./components/admin/tabs/SiteAnalyticsTab'));
const UsersTab = lazy(() => import('./components/admin/tabs/UsersTab'));
const InvitesTab = lazy(() => import('./components/admin/tabs/InvitesTab'));
const BooksTab = lazy(() => import('./components/admin/tabs/BooksTab'));
const OrdersTab = lazy(() => import('./components/admin/tabs/OrdersTab'));
const SupportTab = lazy(() => import('./components/admin/tabs/SupportTab'));
const EmailTemplatesTab = lazy(() => import('./components/admin/tabs/EmailTemplatesTab'));
const EmailWorkflowsTab = lazy(() => import('./components/admin/tabs/EmailWorkflowsTab'));
const EmailLogsTab = lazy(() => import('./components/admin/tabs/EmailLogsTab'));
const ActivityLogsTab = lazy(() => import('./components/admin/tabs/ActivityLogsTab'));
const AdminSettingsTab = lazy(() => import('./components/admin/tabs/SettingsTab'));
const BlogTab = lazy(() => import('./components/admin/tabs/BlogTab'));
const LandingPagesTab = lazy(() => import('./components/admin/tabs/LandingPagesTab'));
const ImageLibraryTab = lazy(() => import('./components/admin/tabs/ImageLibraryTab'));
const InviteAcceptPage = lazy(() => import('./components/invite/InviteAcceptPage'));
const OnboardingPage = lazy(() => import('./components/onboarding/OnboardingPage'));
const TermsPage = lazy(() => import('./components/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./components/legal/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./components/legal/CookiePolicyPage'));
const BlogPage = lazy(() => import('./components/blog/BlogPage'));
const BlogPostPage = lazy(() => import('./components/blog/BlogPostPage'));
const NotFoundPage = lazy(() => import('./components/shared/NotFoundPage'));

// Layout components (small, load eagerly)
import CookieBanner from './components/layout/CookieBanner';
import FeedbackWidget from './components/support/FeedbackWidget';

function PageLoader() {
  return <div className="app-loading" role="status" aria-live="polite">Loading...</div>;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (!user.onboardingComplete) return <Navigate to="/onboarding" />;
  return <>{children}</>;
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (user.onboardingComplete) return <Navigate to="/library" />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/library" />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user && !user.onboardingComplete) return <Navigate to="/onboarding" />;
  return user ? <Navigate to="/library" /> : <>{children}</>;
}

function FeedbackWidgetWrapper() {
  const { user } = useAuth();
  const path = window.location.pathname;
  if (!user || path.startsWith('/admin')) return null;
  return <FeedbackWidget />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><AuthPage mode="login" /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><AuthPage mode="signup" /></PublicRoute>} />

        {/* Onboarding */}
        <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />

        {/* Invite accept (public) */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />

        {/* Shared book reader (public) */}
        <Route path="/read/:shareSlug" element={<BookReader />} />

        {/* Library — standalone (transparent header, no footer) */}
        <Route path="/library" element={<PrivateRoute><Library /></PrivateRoute>} />

        {/* Settings — standalone (transparent header, no footer) */}
        <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />

        {/* Pricing — standalone (transparent header, no footer) */}
        <Route path="/pricing" element={<PrivateRoute><PricingPage /></PrivateRoute>} />

        {/* Orders — standalone (transparent header, no footer) */}
        <Route path="/orders" element={<PrivateRoute><UserOrdersPage /></PrivateRoute>} />

        {/* Book creator — guest can access /create, logged-in users use /create/:id */}
        <Route path="/create" element={<BookCreator />} />
        <Route path="/create/:id" element={<PrivateRoute><BookCreator /></PrivateRoute>} />

        {/* Admin (nested routes — each tab gets its own URL) */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>}>
          <Route index element={<Navigate to="/admin/analytics" replace />} />
          <Route path="analytics" element={<AnalyticsTab />} />
          <Route path="site-analytics" element={<SiteAnalyticsTab />} />
          <Route path="users" element={<UsersTab />} />
          <Route path="invites" element={<InvitesTab />} />
          <Route path="books" element={<BooksTab />} />
          <Route path="orders" element={<OrdersTab />} />
          <Route path="support" element={<SupportTab />} />
          <Route path="email-templates" element={<EmailTemplatesTab />} />
          <Route path="email-workflows" element={<EmailWorkflowsTab />} />
          <Route path="email-logs" element={<EmailLogsTab />} />
          <Route path="logs" element={<ActivityLogsTab />} />
          <Route path="settings" element={<AdminSettingsTab />} />
          <Route path="blog" element={<BlogTab />} />
          <Route path="landing-pages" element={<LandingPagesTab />} />
          <Route path="image-library" element={<ImageLibraryTab />} />
          {/* Editor routes (render in the Outlet area) */}
          <Route path="blog/new" element={<BlogPostEditor />} />
          <Route path="blog/:id" element={<BlogPostEditor />} />
          <Route path="landing-pages/new" element={<LandingPageEditor />} />
          <Route path="landing-pages/:id" element={<LandingPageEditor />} />
          <Route path="email-templates/new" element={<EmailTemplateEditor />} />
          <Route path="email-templates/:id" element={<EmailTemplateEditor />} />
        </Route>

        {/* Blog (public, no auth required) */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Public landing pages */}
        <Route path="/p/:slug" element={<PublicLandingPage />} />

        {/* Legal (public, no auth required) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              fontFamily: "'Nunito', sans-serif",
              fontWeight: 600,
              color: '#5C4A32',
              borderRadius: '0',
              border: 'none',
              boxShadow: 'none',
              background: 'transparent url(/images/blank-banner.png) no-repeat center / 100% 100%',
              padding: '12px 48px 34px',
              minWidth: '280px',
              textAlign: 'center' as const,
              fontSize: '0.9rem',
            },
            icon: null,
            success: {},
            error: {},
          }}
        />
        <FeedbackWidgetWrapper />
        <CookieBanner />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
    </HelmetProvider>
  );
}
